/**
 * 주석(Annotation) 관련 색상 상수
 * 하이라이트, 메모, 밑줄 등의 색상을 중앙에서 관리합니다.
 */

// ============================================
// 메모(Note/Memo) 색상
// ============================================

/** 메모 배경색 */
export const NOTE_BACKGROUND_COLORS = {
  green: '#EBFFCF',
  yellow: '#FEFFCF',
  blue: '#E3F2FF',
} as const;

/** 메모 테두리색 */
export const NOTE_BORDER_COLORS = {
  green: '#C1DC9B',
  yellow: '#E3E4A7',
  blue: '#C2D2E1',
} as const;

/** 메모 색상 타입 */
export type NoteColorType = keyof typeof NOTE_BACKGROUND_COLORS;

/** 배경색으로 색상 타입 찾기 */
export const getNoteColorType = (backgroundColor: string): NoteColorType => {
  if (backgroundColor === NOTE_BACKGROUND_COLORS.green) return 'green';
  if (backgroundColor === NOTE_BACKGROUND_COLORS.blue) return 'blue';
  return 'yellow'; // 기본값
};

/** 색상 타입으로 배경색과 테두리색 가져오기 */
export const getNoteColors = (colorType: NoteColorType) => ({
  background: NOTE_BACKGROUND_COLORS[colorType],
  border: NOTE_BORDER_COLORS[colorType],
});

// ============================================
// 하이라이트(Highlight) 색상
// ============================================

/** 하이라이트 색상 옵션 */
export const HIGHLIGHT_COLORS = {
  yellow: '#FFEB3B',
  green: '#A5D6A7',
  blue: '#90CAF9',
  pink: '#F48FB1',
  orange: '#FFCC80',
} as const;

/** 학습자용 하이라이트/밑줄 색상 (연한 파스텔 계열) */
export const STUDENT_MARKER_COLORS = {
  pink: '#FFE4E8',      // 연한 핑크
  lavender: '#E8E4FF',  // 연한 라벤더
  mint: '#E4FFF0',      // 연한 민트
} as const;

/** 학습자용 마커 색상 타입 */
export type StudentMarkerColorType = keyof typeof STUDENT_MARKER_COLORS;

/** 하이라이트 테두리색 */
export const HIGHLIGHT_BORDER_COLORS = {
  yellow: '#FDD835',
  green: '#81C784',
  blue: '#64B5F6',
  pink: '#F06292',
  orange: '#FFB74D',
} as const;

/** 하이라이트 색상 타입 */
export type HighlightColorType = keyof typeof HIGHLIGHT_COLORS;

/** 기본 하이라이트 색상 */
export const DEFAULT_HIGHLIGHT_COLOR = HIGHLIGHT_COLORS.yellow;

/** 하이라이트 색상으로 테두리색 가져오기 */
export const getHighlightBorderColor = (color: string): string => {
  switch (color) {
    case HIGHLIGHT_COLORS.yellow: return HIGHLIGHT_BORDER_COLORS.yellow;
    case HIGHLIGHT_COLORS.green: return HIGHLIGHT_BORDER_COLORS.green;
    case HIGHLIGHT_COLORS.blue: return HIGHLIGHT_BORDER_COLORS.blue;
    case HIGHLIGHT_COLORS.pink: return HIGHLIGHT_BORDER_COLORS.pink;
    case HIGHLIGHT_COLORS.orange: return HIGHLIGHT_BORDER_COLORS.orange;
    default: return color; // 알 수 없는 색상은 그대로 반환
  }
};

// ============================================
// 밑줄(Underline) 색상
// ============================================

/** 밑줄 색상 */
export const UNDERLINE_COLORS = {
  red: '#ef4444',
  blue: '#3b82f6',
  green: '#22c55e',
} as const;

/** 기본 밑줄 색상 */
export const DEFAULT_UNDERLINE_COLOR = UNDERLINE_COLORS.red;

// ============================================
// 영역 지정(Area Selection) 색상
// ============================================

/** 영역 지정 배경색 */
export const AREA_SELECTION_BACKGROUND = '#FFF0F3';

/** 영역 지정 테두리색 */
export const AREA_SELECTION_BORDER = '#C75355';

// ============================================
// 기본값
// ============================================

/** 기본 메모 색상 */
export const DEFAULT_NOTE_COLOR: NoteColorType = 'yellow';

/** 기본 메모 배경색 */
export const DEFAULT_NOTE_BACKGROUND = NOTE_BACKGROUND_COLORS.yellow;

